import { InspectorControls } from '@wordpress/block-editor';
import {
  PanelBody,
  RangeControl,
  SelectControl,
  ToggleControl,
} from '@wordpress/components';
import { __ } from '@wordpress/i18n';

import SliderControlsPanel from '../../../../utils/slider-controls-panel';

/*
 * The face box is a choice, not a measurement. Every face of a cube is the same
 * size, so a box that grew to whichever slide happened to be tallest would stop
 * being a cube the moment two slides differed. Ratio rather than height because
 * it needs no per-breakpoint value to stay square-on.
 */
const ASPECT_OPTIONS = [
  { label: __('16:9 — widescreen', 'brandy-blocks'), value: '16/9' },
  { label: __('4:3 — classic', 'brandy-blocks'), value: '4/3' },
  { label: __('3:2 — photo', 'brandy-blocks'), value: '3/2' },
  { label: __('1:1 — square', 'brandy-blocks'), value: '1/1' },
  { label: __('3:4 — portrait', 'brandy-blocks'), value: '3/4' },
  { label: __('9:16 — tall', 'brandy-blocks'), value: '9/16' },
];

export default function CubeSliderInspectorControls({ attributes, setAttributes }) {
  const {
    aspectRatio,
    loop,
    autoplay,
    autoplayDelay,
    pauseOnHover,
    showArrows,
    showDots,
  } = attributes;

  return (
    <InspectorControls>
      <PanelBody title={__('Layout', 'brandy-blocks')} initialOpen={true}>
        <SelectControl
          __nextHasNoMarginBottom
          label={__('Face ratio', 'brandy-blocks')}
          help={__(
            'Sets the size of every cube face. All faces share one box — content taller than the box will overflow it rather than resize the cube.',
            'brandy-blocks'
          )}
          value={aspectRatio ?? '16/9'}
          options={ASPECT_OPTIONS}
          onChange={(v) => setAttributes({ aspectRatio: v })}
        />
      </PanelBody>

      <PanelBody title={__('Behavior', 'brandy-blocks')} initialOpen={true}>
        <ToggleControl
          __nextHasNoMarginBottom
          label={__('Loop', 'brandy-blocks')}
          checked={!!loop}
          onChange={(v) => setAttributes({ loop: v })}
        />
        <ToggleControl
          __nextHasNoMarginBottom
          label={__('Autoplay', 'brandy-blocks')}
          help={__(
            'Turns the cube one face at a time on a timer. There is no continuous-drift option: drift never settles on a face, so the cube would sit permanently mid-turn.',
            'brandy-blocks'
          )}
          checked={!!autoplay}
          onChange={(v) => setAttributes({ autoplay: v })}
        />
        {autoplay && (
          <>
            <RangeControl
              __nextHasNoMarginBottom
              label={__('Delay (ms)', 'brandy-blocks')}
              value={autoplayDelay ?? 4000}
              onChange={(v) => setAttributes({ autoplayDelay: v })}
              min={1000}
              max={12000}
              step={500}
            />
            <ToggleControl
              __nextHasNoMarginBottom
              label={__('Pause on hover', 'brandy-blocks')}
              checked={!!pauseOnHover}
              onChange={(v) => setAttributes({ pauseOnHover: v })}
            />
          </>
        )}
        <ToggleControl
          __nextHasNoMarginBottom
          label={__('Show arrows', 'brandy-blocks')}
          checked={!!showArrows}
          onChange={(v) => setAttributes({ showArrows: v })}
        />
        <ToggleControl
          __nextHasNoMarginBottom
          label={__('Show dots', 'brandy-blocks')}
          checked={!!showDots}
          onChange={(v) => setAttributes({ showDots: v })}
        />
      </PanelBody>

      <PanelBody title={__('Controls', 'brandy-blocks')} initialOpen={false}>
        {/*
          showDimControl={false}: the shared panel's "dim outer slides" fades
          whichever slides are not centred, which needs a row of them. This
          block shows one face at a time, and phase 4's face shading already
          owns how a turning face darkens.
        */}
        <SliderControlsPanel
          attributes={attributes}
          setAttributes={setAttributes}
          showDimControl={false}
        />
      </PanelBody>
    </InspectorControls>
  );
}
